import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/** @jsxImportSource @emotion/react */
import { ButtonGroup, Classes, Colors, Popover, Icon, } from '@blueprintjs/core';
import { css } from '@emotion/react';
import { cloneElement, useLayoutEffect, useMemo, useRef, } from 'react';
import { Button } from '../index';
import { toolbarContext, useToolbarContext, } from './toolbarContext';
const border = '1px solid rgb(247, 247, 247)';
export function Toolbar(props) {
    const { children, disabled, intent, large, vertical, popoverInteractionKind, } = props;
    const contextValue = useMemo(() => ({ intent, large, vertical, disabled, popoverInteractionKind }), [intent, large, vertical, disabled, popoverInteractionKind]);
    const ref = useRef(null);
    // Work around wrong width on vertical flex when wrapping
    // In Chrome: recently fixed (https://bugs.chromium.org/p/chromium/issues/detail?id=507397)
    // In Firefox: work-around needed (https://bugzilla.mozilla.org/show_bug.cgi?id=995020)
    // In Safari: work-around needed
    useLayoutEffect(() => {
        if (!vertical) {
            return;
        }
        function update() {
            const lastElement = ref.current?.lastElementChild;
            if (!lastElement) {
                return;
            }
            ref.current.style.width = 'initial';
            const divRect = ref.current.getBoundingClientRect();
            const lastElemRect = lastElement.getBoundingClientRect();
            const width = `${lastElemRect.right - divRect.left}px`;
            if (ref.current.style.width !== width) {
                ref.current.style.width = width;
            }
        }
        const element = ref.current;
        if (element) {
            const observer = new ResizeObserver(update);
            observer.observe(element);
            return () => observer.unobserve(element);
        }
    }, [vertical]);
    return (_jsx(ToolbarProvider, { value: contextValue, children: _jsx(ButtonGroup
        // Reset because of layout effect above
        // TODO: remove once the workaround is no longer needed
        , { vertical: vertical, large: large, style: {
                flexWrap: 'wrap',
                borderRight: vertical ? border : undefined,
            }, children: children }, String(vertical)) }));
}
Toolbar.Item = function ToolbarItem(props) {
    const { active = false, icon, onClick, tooltip, tooltipProps, id, intent: itemIntent, disabled: itemDisabled, isPopover, ...other } = props;
    const { intent: toolbarIntent, disabled: toolbarDisabled, large, vertical, } = useToolbarContext();
    const intent = itemIntent ?? toolbarIntent;
    const disabled = itemDisabled ?? toolbarDisabled;
    const resizedIcon = typeof icon === 'string'
        ? icon
        : cloneElement(icon, {
            className: icon.props.className
                ? `${icon.props.className} bp5-icon`
                : 'bp5-icon',
        });
    return (_jsx(Button, { alignText: isPopover ? 'left' : undefined, minimal: true, disabled: disabled, css: css `
        .${Classes.ICON} {
          color: ${Colors.DARK_GRAY3};
        }
      `, intent: intent, style: {
            position: 'relative',
            fontSize: '1.125em',
            width: 'fit-content',
            flex: 'none',
        }, type: "button", active: active, icon: _jsxs("div", { style: {
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: 0,
                height: 0,
            }, children: [_jsx(Icon, { icon: resizedIcon }), isPopover && (_jsx(Icon, { icon: "caret-right", size: large ? 14 : 9, style: {
                        transform: 'rotate(45deg)',
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                    } }))] }), onClick: (event) => {
            onClick?.({ event, ...props });
        }, tooltipProps: !tooltip
            ? undefined
            : {
                content: tooltip,
                placement: vertical ? 'right' : 'bottom',
                intent,
                compact: !large,
                interactionKind: 'hover',
                ...tooltipProps,
            }, ...other }));
};
Toolbar.PopoverItem = function ToolbarPopoverItem(props) {
    const { itemProps, ...other } = props;
    const { disabled, vertical, popoverInteractionKind } = useToolbarContext();
    return (_jsx(Popover, { minimal: true, disabled: disabled, placement: vertical ? 'right-start' : 'bottom-start', interactionKind: popoverInteractionKind, hoverCloseDelay: 0, css: css `
        .${Classes.ICON} {
          color: ${Colors.DARK_GRAY3};
        }
      `, targetProps: {
            style: {
                position: 'relative',
                fontSize: '1.125em',
                width: 'fit-content',
                height: 'fit-content',
                flex: 'none',
            },
        }, ...other, children: _jsx(Toolbar.Item, { isPopover: true, ...itemProps }) }));
};
function ToolbarProvider(props) {
    return (_jsx(toolbarContext.Provider, { value: props.value, children: props.children }));
}
//# sourceMappingURL=Toolbar.js.map